import type { HealthCheckDetail, HealthCopy } from '@/frontend/features/Fde/dataHealth/types'
import { format, formatNumber } from '@/frontend/features/Fde/dataHealth/types'

type Bin = NonNullable<HealthCheckDetail['histogram']>[number]

// Value distribution of a column with the common range [low, high] marked; bins outside it in red.
export function DistributionChart({ histogram, low, high, copy }: { histogram: Bin[]; low: number | null; high: number | null; copy: HealthCopy }) {
  const bins = histogram.filter((bin) => Number.isFinite(bin.lower) && Number.isFinite(bin.upper) && bin.upper >= bin.lower)
  if (!bins.length) return null
  const d0 = Math.min(...bins.map((bin) => bin.lower))
  const d1 = Math.max(...bins.map((bin) => bin.upper))
  const span = Math.max(d1 - d0, Number.EPSILON)
  const x = (value: number) => Math.min(100, Math.max(0, ((value - d0) / span) * 100))
  const max = Math.max(1, ...bins.map((bin) => bin.count))
  const outside = (bin: Bin) => (low != null && bin.upper <= low) || (high != null && bin.lower >= high)
  const band = low != null || high != null ? { from: x(low ?? d0), to: x(high ?? d1) } : null

  return (
    <figure className="m-0 grid gap-1.5">
      <div className="text-[11px] text-muted-foreground">{copy.chart_distribution}</div>
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="h-28 w-full border-b border-border" role="img" aria-label={copy.chart_distribution}>
        {band && <rect x={band.from} y={0} width={Math.max(0.3, band.to - band.from)} height={100} className="fill-foreground/5" />}
        {bins.map((bin, at) => {
          const left = x(bin.lower)
          const width = Math.max(0.3, x(bin.upper) - left - 0.3)
          // a bin with rows never disappears, even next to a very tall one
          const height = bin.count > 0 ? Math.max(2, (bin.count / max) * 100) : 0
          return (
            <g key={at}>
              <title>{`${format(copy.range, { from: formatNumber(bin.lower), to: formatNumber(bin.upper) })}: ${format(copy[`rows_${bin.count === 1 ? 'one' : 'other'}`], { count: formatNumber(bin.count) })}`}</title>
              <rect x={left} y={0} width={Math.max(0.3, x(bin.upper) - left)} height={100} fill="transparent" />
              {height > 0 && <rect x={left} y={100 - height} width={width} height={height} className={outside(bin) ? 'fill-red-500' : 'fill-primary/60'} />}
            </g>
          )
        })}
        {band && [band.from, band.to].map((edge, at) => (
          <line key={at} x1={edge} x2={edge} y1={0} y2={100} className="stroke-foreground/50" strokeWidth={1} strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />
        ))}
      </svg>
      <div className="flex justify-between text-[11px] text-muted-foreground tabular-nums">
        <span>{formatNumber(d0)}</span>
        <span>{formatNumber(d1)}</span>
      </div>
      <figcaption className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
        {band && (
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block size-2.5 rounded-sm border border-dashed border-foreground/50 bg-foreground/5" />
            {format(copy.chart_common_range, { low: formatNumber(low ?? d0), high: formatNumber(high ?? d1) })}
          </span>
        )}
        <span className="inline-flex items-center gap-1.5"><span className="inline-block size-2.5 rounded-sm bg-red-500" />{copy.chart_outside_range}</span>
      </figcaption>
    </figure>
  )
}
